import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      position: "CTO",
      company: "",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      content: `Fawas is an exceptional developer who consistently delivers high-quality solutions. His ability to understand complex requirements and translate them into elegant code is remarkable. During our collaboration, he led the development of our core platform, which now serves over 500,000 users daily.`,
      project: "E-commerce Platform Redesign",
      date: "September 2023"
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      position: "Product Manager",
      company: "",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      content: `Working with  was a game-changer for our startup. He not only delivered exceptional code but also provided valuable insights on architecture and scalability. His mentorship helped our junior developers grow significantly, and his technical leadership was instrumental in our successful Series A funding.`,
      project: "SaaS Platform Development",
      date: "July 2023"
    },
    {
      id: 3,
      name: "Emily Watson",
      position: "Senior Designer",
      company: "",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      content: `Fawas has an incredible ability to bring designs to life with pixel-perfect precision. His attention to detail and understanding of user experience principles made our collaboration seamless. The final product exceeded our expectations and received outstanding user feedback.`,
      project: "Mobile App UI Implementation",
      date: "May 2023"
    },
    {
      id: 4,
      name: "David Kim",
      position: "Engineering Director",
      company: "",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      content: `Fawas's expertise in cloud architecture and DevOps practices helped us achieve 99.99% uptime for our critical systems. His proactive approach to identifying and solving potential issues before they impact users is truly valuable. I would definitely work with him again.`,
      project: "Cloud Infrastructure Migration",
      date: "March 2023"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      position: "Founder & CEO",
      company: "",
      avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      content: ` Fawas joined our team during a critical phase and immediately made an impact. His full-stack expertise allowed us to move fast while maintaining code quality. He's not just a great developer but also an excellent communicator who can explain complex technical concepts to non-technical stakeholders.`,
      project: "MVP Development & Launch",
      date: "January 2023"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials?.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials?.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === testimonials?.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? "text-warning fill-current" : "text-muted"}
      />
    ));
  };

  return (
    <section className="bg-background py-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Client Testimonials
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            What clients and colleagues say about working with me
          </p>
        </div>

        <div className="relative">
          {/* Main Testimonial */}
          <div className="bg-surface rounded-2xl shadow-moderate p-8 lg:p-12 mb-8">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={testimonials?.[currentIndex]?.avatar}
                  alt={`${testimonials?.[currentIndex]?.name} avatar`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  {renderStars(testimonials?.[currentIndex]?.rating)}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  {testimonials?.[currentIndex]?.name}
                </h3>
                <p className="text-primary font-medium mb-1">
                  {testimonials?.[currentIndex]?.position}
                </p>
                <p className="text-muted-foreground text-sm">
                  {testimonials?.[currentIndex]?.company}
                </p>
              </div>
            </div>

            <blockquote className="text-lg text-muted-foreground leading-relaxed mb-6">
              "{testimonials?.[currentIndex]?.content}"
            </blockquote>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="font-medium">
                Project: {testimonials?.[currentIndex]?.project}
              </span>
              <span>{testimonials?.[currentIndex]?.date}</span>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              className="w-12 h-12"
            >
              <Icon name="ChevronLeft" size={20} />
            </Button>

            {/* Dots Indicator */}
            <div className="flex items-center space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentIndex
                      ? 'bg-primary scale-125' :'bg-border hover:bg-muted-foreground'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              className="w-12 h-12"
            >
              <Icon name="ChevronRight" size={20} />
            </Button>
          </div>

          {/* Auto-play indicator */}
          <div className="flex items-center justify-center mt-6">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon 
                name={isAutoPlaying ? "Pause" : "Play"} 
                size={16} 
              />
              <span>{isAutoPlaying ? 'Auto-playing' : 'Paused'}</span>
            </button>
          </div>
        </div>

        {/* Mobile Swipe Hint */}
        <div className="md:hidden text-center mt-6">
          <p className="text-sm text-muted-foreground flex items-center justify-center">
            <Icon name="Smartphone" size={16} className="mr-2" />
            Swipe left or right to navigate
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;